import { useSearchParams } from "react-router-dom";
import { RefreshCw, RotateCcw, SlidersHorizontal } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import JobMembersCard from "./JobMembersCard";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import useConversationGroups from "../useConversationGroups";
import { IRecentRoom, IMessageJobMembersProfile } from "../../../endpoints/types/message.type";
import { EnumUserTypes } from "../../../utils/constants";
import { IRoomGroupParams } from "../../../endpoints/job.service";

type Category = NonNullable<IRoomGroupParams["conversationCategory"]>;
const categories: { value: Category; label: string }[] = [
  { value: "applicants", label: "Applicants" },
  { value: "companies", label: "Hiring Companies" },
  { value: "referrals", label: "Referrals" },
];

function categoryFromRoute(params: URLSearchParams): Category {
  const value = params.get("conversationCategory");
  if (categories.some(category => category.value === value)) return value as Category;
  const context = params.get("jobId") || "";
  if (context.startsWith("requirement:")) return "companies";
  if (context.startsWith("network:")) return "referrals";
  return "applicants";
}

type SelectedContact = IRecentRoom & { participantDetails: IMessageJobMembersProfile["participantDetails"] };
type MembersProfileSectionProps = {
  selectedContact: SelectedContact | null;
  setSelectedContact: (contact: SelectedContact | null) => void;
};

const MembersProfileSection = ({ selectedContact, setSelectedContact }: MembersProfileSectionProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeJobId = searchParams.get("jobId");
  const routeParticipantId = searchParams.get("participantId");
  const initialSelection = useRef<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [jobSearchText, setJobSearchText] = useState("");
  const [jobId, setJobId] = useState("");
  const [selectedJobLabel, setSelectedJobLabel] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const debouncedSearch = useDebounce(searchText, 350);
  const debouncedJobSearch = useDebounce(jobSearchText, 350);
  // Keep filtered results separate from the global header unread-badge state.
  const revision = useSelector((state: RootState) => state.messageReducer?.membersProfile);
  const user = useSelector((state: RootState) => state.auth?.user);
  const isRecruiter = user?.userType === EnumUserTypes.RECRUITER;
  const category = isRecruiter ? categoryFromRoute(searchParams) : undefined;
  const previousCategory = useRef(category);
  const categoryChanged = previousCategory.current !== category;
  const { groups, jobPosts, counts, pagination, loading, refreshing, loadingMore, error, loadMore, retry, retryFailed } = useConversationGroups({
    ...(category ? { conversationCategory: category } : {}),
    groupType: "participant", limit: 20, unreadOnly, activeOnly,
    searchText: debouncedSearch, jobSearchText: debouncedJobSearch, jobId,
  }, revision);

  useEffect(() => {
    if (previousCategory.current === category) return;
    previousCategory.current = category;
    initialSelection.current = null;
    setSelectedContact(null);
    setJobId("");
    setJobSearchText("");
    setSelectedJobLabel("");
  }, [category, setSelectedContact]);

  useEffect(() => {
    const selectionKey = `${category}:${routeJobId}:${routeParticipantId}`;
    if (categoryChanged || initialSelection.current === selectionKey || !routeJobId || loading) return;
    const group = groups.find((item) => (!routeParticipantId ||
      (item.participantDetails?.userId || item.participantDetails?._id) === routeParticipantId) &&
      item.recentRooms?.some((room) => room.jobId === routeJobId));
    const room = group?.recentRooms?.find((item) => item.jobId === routeJobId);
    if (room) {
      initialSelection.current = selectionKey;
      setSelectedContact({ ...room, participantDetails: group.participantDetails });
    }
  }, [groups, routeJobId, routeParticipantId, loading, setSelectedContact, category, categoryChanged]);

  const changeCategory = (value: Category) => {
    if (value === category) return;
    const next = new URLSearchParams(searchParams);
    next.set("conversationCategory", value);
    next.delete("jobId");
    next.delete("participantId");
    setSelectedContact(null);
    setJobId("");
    setJobSearchText("");
    setSelectedJobLabel("");
    initialSelection.current = null;
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchText("");
    setJobSearchText("");
    setJobId("");
    setSelectedJobLabel("");
    setUnreadOnly(false);
    setActiveOnly(false);
  };

  return (
    <section aria-label="Conversations" className="border-r border-[#ECECEC] w-full min-w-0 md:w-4/12 md:min-w-[300px] md:max-w-[420px] flex flex-col min-h-0 max-h-[55vh] md:max-h-none">
      <div className="border-b border-[#ECECEC] p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold text-sm text-charcoal">Conversations</h2>
          <button type="button" aria-label="Toggle conversation filters" aria-expanded={showFilters}
            aria-controls="conversation-filters" onClick={() => setShowFilters((value) => !value)}
            className="p-2 rounded-md hover:bg-gray-100 text-[#47556d]">
            <SlidersHorizontal size={18} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 text-[11px] text-light-black">
          <p aria-live="polite">Unread {counts.unreadCount} | Filtered {counts.filteredCount} | Total {counts.totalCount}</p>
          <button type="button" onClick={retry} disabled={loading || refreshing}
            aria-label="Refresh conversations" title={refreshing ? "Updating conversations..." : "Refresh"}
            className="p-2 rounded-md text-[#47556d] hover:bg-gray-100 disabled:opacity-50">
            <RefreshCw size={16} aria-hidden="true" className={refreshing ? "animate-spin motion-reduce:animate-none" : ""} />
          </button>
        </div>
        {isRecruiter && <div role="tablist" aria-label="Conversation types" className="flex w-full min-w-0 overflow-x-auto rounded-md bg-[#edf1f4] p-1 gap-1">
          {categories.map((item, index) => <button key={item.value} type="button" role="tab"
            id={`conversation-tab-${item.value}`} aria-controls="conversation-list" tabIndex={category === item.value ? 0 : -1}
            aria-selected={category === item.value} onClick={() => changeCategory(item.value)}
            onKeyDown={event => {
              const next = event.key === "ArrowRight" ? (index + 1) % categories.length
                : event.key === "ArrowLeft" ? (index + categories.length - 1) % categories.length
                : event.key === "Home" ? 0 : event.key === "End" ? categories.length - 1 : -1;
              if (next < 0) return;
              event.preventDefault();
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
              changeCategory(categories[next].value);
            }}
            className={`flex-auto shrink-0 min-w-max whitespace-nowrap rounded px-1.5 py-2 text-[11px] font-semibold transition-colors ${category === item.value ? "bg-white text-[#21364b] shadow-sm" : "text-[#47556d] hover:bg-white/60"}`}>
            {item.label}
          </button>)}
        </div>}
        {showFilters && (
          <div id="conversation-filters" className="rounded-md border border-[#dce2ea] bg-[#f5f7f9] p-3 space-y-3 text-xs">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} /> Unread only
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer" title="Conversations with users who are online">
                <input type="checkbox" checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} /> Active only (online)
              </label>
              <button type="button" onClick={resetFilters} aria-label="Reset filters" title="Reset"
                className="p-2 rounded-md text-[#47556d] hover:bg-gray-100 ml-auto">
                <RotateCcw size={16} aria-hidden="true" />
              </button>
            </div>
            <label className="block space-y-1 text-[#47556d]">
              <span>Search rooms</span>
              <input value={searchText} onChange={(event) => setSearchText(event.target.value)}
                placeholder="Name, job or reference" type="search"
                className="w-full min-w-0 rounded border border-[#dce2ea] bg-white px-2 py-2 outline-none focus:border-[#47556d]" />
            </label>
            <label className="block space-y-1 text-[#47556d]">
              <span>Search job posts</span>
              <input value={jobSearchText} onChange={(event) => setJobSearchText(event.target.value)}
                placeholder="Job title, company or reference" type="search"
                className="w-full min-w-0 rounded border border-[#dce2ea] bg-white px-2 py-2 outline-none focus:border-[#47556d]" />
            </label>
            <label className="block space-y-1 text-[#47556d]">
              <span>Job post</span>
              <select value={jobId} onChange={(event) => {
                setJobId(event.target.value);
                setSelectedJobLabel(event.target.selectedOptions[0]?.textContent || "");
              }} className="w-full min-w-0 rounded border border-[#dce2ea] bg-white pl-2 pr-8 py-2">
                <option value="">All job posts</option>
                {jobId && !jobPosts.some((job) => job._id === jobId) && <option value={jobId}>{selectedJobLabel}</option>}
                {jobPosts.map((job) => <option key={job._id} value={job._id}>
                  {[job.title, job.companyName, job.referenceId].filter(Boolean).join(" - ")}
                </option>)}
              </select>
            </label>
          </div>
        )}
      </div>
      <div id="conversation-list" role={isRecruiter ? "tabpanel" : undefined}
        aria-labelledby={isRecruiter ? `conversation-tab-${category}` : undefined}
        className="overflow-y-auto flex-grow no-scrollbar min-h-0" aria-busy={loading || refreshing || loadingMore}>
        {loading && !groups.length && <p className="p-4 text-xs text-light-black" role="status">Loading conversations...</p>}
        {groups.map((item) => (
          <JobMembersCard key={item._id} item={item} userId={user?._id} selectedContact={selectedContact} setSelectedContact={setSelectedContact} />
        ))}
        {!loading && !error && !groups.length && <p className="p-4 text-xs text-light-black">No conversations match these filters.</p>}
        {error && <div className="p-4 text-xs" role="alert">
          <p>{error}</p><button type="button" onClick={retryFailed} className="mt-2 underline">Try again</button>
        </div>}
        {!loading && pagination.hasNextPage && <button type="button" onClick={loadMore} disabled={loadingMore || refreshing}
          className="m-3 rounded-md border border-[#dce2ea] px-4 py-2 text-xs disabled:opacity-50">
          {loadingMore ? "Loading..." : "Load more conversations"}
        </button>}
      </div>
    </section>
  );
};

export default MembersProfileSection;
